import { create } from "zustand";
import type {
  AuthUser,
  AuthStep,
  Role,
  TenantBranding,
  TenantType,
  CognitoConfig,
} from "@/types/auth";

interface AuthState {
  user: AuthUser | null;
  role: Role | null;
  tenantId: string | null;
  tenantName: string | null;
  tenantType: TenantType | null;
  branding: TenantBranding | null;
  cognitoConfig: CognitoConfig | null;
  step: AuthStep;
  isAuthenticated: boolean;
  isInitializing: boolean;
  error: string | null;
}

interface AuthActions {
  setAuth: (user: AuthUser) => void;
  clearAuth: () => void;
  setTenantContext: (params: {
    tenantId: string;
    tenantName: string;
    tenantType: TenantType;
    branding: TenantBranding;
  }) => void;
  setCognitoConfig: (config: CognitoConfig) => void;
  setStep: (step: AuthStep) => void;
  setInitializing: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialState: AuthState = {
  user: null,
  role: null,
  tenantId: null,
  tenantName: null,
  tenantType: null,
  branding: null,
  cognitoConfig: null,
  step: "idle",
  isAuthenticated: false,
  isInitializing: true,
  error: null,
};

export const useAuthStore = create<AuthState & AuthActions>()((set) => ({
  ...initialState,

  setAuth: (user) =>
    set({
      user,
      role: user.role,
      tenantId: user.tenantId,
      tenantName: user.tenantName,
      tenantType: user.tenantType,
      isAuthenticated: true,
      step: "authenticated",
      error: null,
    }),

  clearAuth: () =>
    set({
      user: null,
      role: null,
      isAuthenticated: false,
      step: "login",
      error: null,
    }),

  setTenantContext: ({ tenantId, tenantName, tenantType, branding }) =>
    set({ tenantId, tenantName, tenantType, branding }),

  setCognitoConfig: (cognitoConfig) => set({ cognitoConfig }),

  setStep: (step) => set({ step }),

  setInitializing: (isInitializing) => set({ isInitializing }),

  setError: (error) => set({ error }),
}));
